import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { APP_SHORT_LABELS, isAppKey, type AppKey, type NotificationChannel } from '@qms360/shared';
import { AppTopBar, Breadcrumbs } from '../components/AppTopBar.js';
import { PushDeviceControl } from '../components/PushDeviceControl.js';
import { PageMessage } from './AppHome.js';
import { ApiError } from '../lib/api.js';
import {
  fetchInbox,
  fetchPreferences,
  markAllNotificationsRead,
  markNotificationRead,
  savePreferences,
  type InboxNotification,
} from '../lib/notifications-api.js';
import { t, formatDateTime } from '../lib/i18n.js';

/**
 * The Notification Center (spec §13.6) — a person's own messages, from all
 * three applications, and how they want to receive them.
 *
 * One inbox across the apps, reached from each app's own bar: an overdue CAR is
 * news to its owner whichever app they happen to be in. Each message says which
 * app it came from and opens there.
 */

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  in_app: 'In the app',
  email: 'Email',
  push: 'Push to this device',
  sms: 'SMS',
};

function when(iso: string): string {
  return formatDateTime(iso, { dateStyle: 'medium', timeStyle: 'short' });
}

export function Notifications() {
  const params = useParams<{ app: string }>();
  const queryClient = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inbox = useInfiniteQuery({
    queryKey: ['notifications', 'inbox', unreadOnly],
    queryFn: ({ pageParam }) => fetchInbox({ before: pageParam, unread: unreadOnly }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };
  const fail = (fallback: string) => (caught: unknown) =>
    setError(caught instanceof ApiError ? caught.message : fallback);

  const read = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: refresh,
    onError: fail(t("That message could not be marked read.")),
  });
  const readAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: refresh,
    onError: fail(t("Your messages could not be marked read.")),
  });

  const app = params.app;
  if (!app || !isAppKey(app)) {
    return <PageMessage title={t("Unknown application")} body="That application does not exist." />;
  }

  const entries = inbox.data?.pages.flatMap((page) => page.notifications) ?? [];

  return (
    <div className="min-h-full">
      <AppTopBar app={app as AppKey} />
      <Breadcrumbs app={app as AppKey} trail={['Notifications']} />

      <main className="mx-auto max-w-4xl px-4 pb-16 pt-4">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h1 className="m-0 text-2xl font-semibold">{t("Notifications")}</h1>
          <div className="flex items-center gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={unreadOnly} onChange={(event) => setUnreadOnly(event.target.checked)} />
              {t("Unread only")}
            </label>
            <button
              type="button"
              disabled={readAll.isPending}
              onClick={() => readAll.mutate()}
              className="rounded border border-border px-3 py-1.5 disabled:opacity-60"
            >
              {t("Mark all read")}
            </button>
          </div>
        </div>
        <p className="mt-1 text-sm text-content-secondary">
          {t("Reminders, overdue items and escalations from all three applications.")}
        </p>

        {error && (
          <p role="alert" className="mt-4 text-sm" style={{ color: 'var(--tone-rejected)' }}>
            {error}
          </p>
        )}
        {inbox.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}
        {inbox.error && (
          <p role="alert" className="mt-6 text-sm" style={{ color: 'var(--tone-rejected)' }}>
            {t("Your notifications could not be loaded.")}
          </p>
        )}
        {!inbox.isLoading && !inbox.error && entries.length === 0 && (
          <p className="mt-6 rounded-md border border-border bg-surface p-5 text-sm text-content-secondary" role="status">
            {unreadOnly ? t("Nothing unread.") : t("Nothing yet. Reminders and escalations for your work will appear here.")}
          </p>
        )}

        <ul className="m-0 mt-4 list-none space-y-2 p-0">
          {entries.map((entry) => (
            <NotificationRow key={entry.id} entry={entry} onRead={() => read.mutate(entry.id)} />
          ))}
        </ul>

        {inbox.hasNextPage && (
          <button
            type="button"
            disabled={inbox.isFetchingNextPage}
            onClick={() => void inbox.fetchNextPage()}
            className="mt-4 rounded border border-border px-3 py-1.5 text-sm disabled:opacity-60"
          >
            {inbox.isFetchingNextPage ? t("Loading…") : t("Older")}
          </button>
        )}

        <PreferencesSection />
      </main>
    </div>
  );
}

function NotificationRow({ entry, onRead }: { entry: InboxNotification; onRead: () => void }) {
  const unread = !entry.readAt;
  const source = entry.app && isAppKey(entry.app) ? APP_SHORT_LABELS[entry.app] : null;

  return (
    <li
      className={`rounded border p-3 text-sm ${unread ? 'border-ag-purple bg-surface' : 'border-border'}`}
      data-testid="notification"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className={unread ? 'font-semibold' : ''}>{entry.subject}</span>
        <span className="text-xs text-content-secondary">
          {source ? `${source} · ` : ''}
          {when(entry.createdAt)}
        </span>
      </div>
      <p className="mb-0 mt-1 text-content-secondary">{entry.body}</p>
      <div className="mt-2 flex gap-3">
        {entry.linkPath?.startsWith('/') && (
          <Link to={entry.linkPath} onClick={() => unread && onRead()} className="text-ag-purple">
            {t("Open")}
          </Link>
        )}
        {unread && (
          <button type="button" onClick={onRead} className="text-ag-purple underline">
            {t("Mark read")}
          </button>
        )}
      </div>
    </li>
  );
}

function PreferencesSection() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const preferences = useQuery({ queryKey: ['notifications', 'preferences'], queryFn: fetchPreferences });

  const save = useMutation({
    mutationFn: savePreferences,
    onSuccess: (data) => {
      setError(null);
      queryClient.setQueryData(['notifications', 'preferences'], data);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : t("Your preferences could not be saved.")),
  });

  if (!preferences.data) return null;

  return (
    <section className="mt-10 rounded-md border border-border p-5" aria-labelledby="preferences-heading">
      <h2 id="preferences-heading" className="m-0 text-base font-semibold">
        {t("How you are told")}
      </h2>
      <ul className="m-0 mt-3 list-none space-y-2 p-0 text-sm">
        {preferences.data.preferences.map((preference) => (
          <li key={preference.channel}>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={preference.isEnabled}
                disabled={!preference.available || save.isPending}
                onChange={(event) =>
                  save.mutate([{ channel: preference.channel, isEnabled: event.target.checked }])
                }
              />
              {t(CHANNEL_LABELS[preference.channel])}
              {!preference.available && (
                <span className="text-xs text-content-secondary">{t("— not set up on this server yet")}</span>
              )}
            </label>
          </li>
        ))}
      </ul>
      {error && (
        <p role="alert" className="mb-0 mt-3 text-sm" style={{ color: 'var(--tone-rejected)' }}>
          {error}
        </p>
      )}
      <PushDeviceControl />
      <p className="mb-0 mt-4 text-xs text-content-secondary" data-testid="critical-note">
        {t("These always reach you, whatever you choose above:")}{' '}
        {preferences.data.criticalAlwaysDelivered.join(', ')}.
      </p>
    </section>
  );
}
